import * as React from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  Loader2, AlertTriangle, FileText, ExternalLink,
  UserRound, Radar, Target, Building2, Shield, Braces, ListTree, Lock,
  Users, Clock, Gavel,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { CollapsibleSection } from '@/components/ui/collapsible-section'
import {
  Sheet, SheetClose, SheetContent, SheetTitle, SheetDescription,
} from '@/components/ui/sheet'
import { useOrg } from '@/contexts/OrgContext'
import { useFeatureFlag } from '@/hooks/useFeatureFlag'
import { supabase } from '@/integrations/supabase/client'
import { VARIABLE_LABELS } from '@/lib/prompt-variable-labels'

// Reproduces AIPrompts.tsx:601 renderWithVariables — splits on {{var}} and paints
// each variable as a purple chip so the reader sees exactly what gets interpolated.
function renderWithVariables(text: string): React.ReactNode[] {
  const parts = text.split(/(\{\{[^}]+\}\})/g)
  return parts.map((part, i) => {
    // Match dotted/namespaced keys too ({{lead.company}}), not only \w — otherwise
    // the split painted them as chips-worth but the match dropped them back to raw
    // text while the "con variables" badge still counted them.
    const match = part.match(/^\{\{([^}]+)\}\}$/)
    if (match) {
      const varKey = match[1].trim()
      return (
        <span
          key={i}
          className="inline-flex items-center bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 px-1.5 py-0.5 rounded text-[11px] font-semibold mx-0.5"
        >
          {VARIABLE_LABELS[varKey] || varKey}
        </span>
      )
    }
    return <React.Fragment key={i}>{part}</React.Fragment>
  })
}

// ── What "Ver prompt" hands us: the step + its prompt summary (from metrics). ──
export interface PromptDrawerStep {
  stepId: string
  dayOffset: number
  stepType: string
  stepLabel: string
  promptId: string | null
  promptName: string | null
  promptType: string | null
  tone: string | null
  language: string | null
  objective: string | null
  bodyLength: number
  hasVariables: boolean
  // How many steps of the cadence reference this same prompt. When > 1, editing
  // it changes every one of them, so the drawer surfaces a shared-edit warning.
  sharedByStepCount?: number
  // [D] Per-step injection reality (from the FlowStep, not assumed). The
  // "Qué más se inyecta" list must reflect THIS day: `hasAngle` is
  // completeness.tiene_angle (a day with no touch_angles row injects no
  // angle-lock at runtime); `hasResearchPrompt` is true only when the step
  // actually resolves a research prompt injected as {{research}}.
  hasAngle?: boolean
  hasResearchPrompt?: boolean
  // [D] The step-specific rubric that Carlos applies to THIS touch (the
  // system_prompt_addendum, e.g. the Day-9 ban on generic intros). Shown here
  // as a READ-ONLY transparency surface — "cómo se juzga este paso" — so the
  // doctrine is legible without opening the editable Política textarea. Empty
  // / absent → the block is not rendered.
  carlosAddendum?: string | null
  // [D] The EFFECTIVE Carlos values that actually govern this step (the column
  // that MANDA over the prose). The addendum prose can drift (say "Threshold
  // 8.0") from the effective threshold (7.0). We render these next to the prose
  // so the reader always sees what Carlos really applies — the value beats the
  // text. Null = no effective rubric resolved; undefined = not passed.
  carlosThreshold?: number | null
  carlosMinAcceptable?: number | null
  carlosMaxAttempts?: number | null
}

// [D] LABELED drift detector — reuses the PROVEN approach from StepRubricTable's
// thresholdsMentionedIn: the number must sit within ≤12 chars AFTER an explicit
// threshold LABEL ('threshold'/'umbral'/'mínimo'/'minimo'). We deliberately do
// NOT match bare digits — that was the iter11 bug (it grabbed the DAY number and
// misfired on 5/7 steps). Requiring the label is what keeps this honest. Bounded
// to the 0–10 score scale (a "$25" or "2024" that slipped past the window is not
// a rubric threshold). Returns the first labeled number that DIFFERS from the
// effective threshold, or null when nothing labeled contradicts it.
function findLabeledThresholdDrift(prose: string, effectiveThreshold: number): number | null {
  // WORD 'threshold'/'umbral'/'mínimo'/'minimo' → ≤12 non-digit chars → decimal.
  const re = /(?:threshold|umbral|m[íi]nimo)[^0-9]{0,12}(\d+(?:\.\d+)?)/gi
  let m: RegExpExecArray | null
  while ((m = re.exec(prose)) !== null) {
    const n = Number(m[1])
    if (!Number.isFinite(n)) continue
    if (n < 0 || n > 10) continue
    if (n !== effectiveThreshold) return n
  }
  return null
}

// Full row read on demand (the summary carries no body to keep the list light).
interface PromptRow {
  id: string
  name: string | null
  prompt_body: string
  tone: string | null
  language: string | null
  objective: string | null
  structure: string | null
  writing_principles: string[]
  anti_patterns: string[]
  updated_at: string | null
}

function asStringArray(v: unknown): string[] {
  if (!Array.isArray(v)) return []
  return v.filter((x): x is string => typeof x === 'string' && x.trim().length > 0)
}

// es-ES freshness label for the prompt's updated_at (e.g. "3 jul 2026"). Returns
// null on a missing/unparseable timestamp so the header simply omits it.
function formatUpdatedAt(iso: string | null): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })
}

export function PromptDrawer({
  step,
  open,
  onOpenChange,
}: {
  step: PromptDrawerStep | null
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { orgId } = useOrg()
  const promptId = step?.promptId ?? null
  // [D] Step-specific Carlos doctrine (system_prompt_addendum). Rendered as a
  // read-only transparency surface only when non-empty; whitespace-only counts
  // as absent so we never paint an empty block.
  const carlosAddendum = step?.carlosAddendum?.trim() ? step.carlosAddendum.trim() : null
  // [D] The EFFECTIVE values Carlos applies to this step. These are the column
  // that MANDA — shown next to the addendum prose so a drifted instruction
  // ("Threshold 8.0" when the effective threshold is 7.0) can never mislead.
  // null threshold = no effective rubric resolved → we don't fabricate one.
  const carlosThreshold = step?.carlosThreshold ?? null
  const carlosMinAcceptable = step?.carlosMinAcceptable ?? null
  const carlosMaxAttempts = step?.carlosMaxAttempts ?? null
  const hasEffectiveRubric = carlosThreshold !== null
  // [D] Labeled drift: does the addendum prose mention a threshold LABEL followed
  // by a number ≠ the effective threshold? Only computed when both the prose and
  // an effective threshold exist. Requires the label word (never bare digits) so
  // the DAY number can't misfire — that was the iter11 bug we reverted.
  const proseThresholdDrift =
    carlosAddendum && hasEffectiveRubric
      ? findLabeledThresholdDrift(carlosAddendum, carlosThreshold)
      : null
  // /ai-prompts is gated by section_ai_prompts (App.tsx FeatureRoute). If the org
  // has it off, deep-linking there dead-ends on the home page with no message, so
  // we hide the edit link entirely and surface an honest muted note instead.
  const canEditPrompts = useFeatureFlag('section_ai_prompts')

  // Read the full prompt body on demand, org-scoped (RLS enforces isolation).
  const promptQuery = useQuery({
    queryKey: ['autom-prompt-body', orgId, promptId],
    enabled: open && !!orgId && !!promptId,
    queryFn: async (): Promise<PromptRow | null> => {
      const { data, error } = await supabase
        .from('ai_prompts')
        .select('id, name, prompt_body, tone, language, objective, structure, writing_principles, anti_patterns, updated_at')
        .eq('org_id', orgId)
        .eq('id', promptId)
        .maybeSingle()
      if (error) throw new Error(error.message)
      if (!data) return null
      return {
        id: data.id as string,
        name: (data.name as string | null) ?? null,
        prompt_body: (data.prompt_body as string | null) ?? '',
        tone: (data.tone as string | null) ?? null,
        language: (data.language as string | null) ?? null,
        objective: (data.objective as string | null) ?? null,
        structure: (data.structure as string | null) ?? null,
        writing_principles: asStringArray(data.writing_principles),
        anti_patterns: asStringArray(data.anti_patterns),
        updated_at: (data.updated_at as string | null) ?? null,
      }
    },
  })

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="gap-0">
        {step && (
          <>
            {/* Header */}
            <div className="border-b px-5 py-4">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-primary shrink-0" />
                <SheetTitle className="text-base font-semibold leading-tight">
                  {step.promptName ?? 'Prompt del paso'}
                </SheetTitle>
              </div>
              <SheetDescription className="mt-1 text-xs">
                Day {step.dayOffset} · {step.stepLabel} — vista previa de solo lectura del prompt que
                redacta este touch.
              </SheetDescription>
              {/* Freshness: how recently the prompt copy was edited. Read from the
                  on-demand row (updated_at); omitted while loading or if absent. */}
              {formatUpdatedAt(promptQuery.data?.updated_at ?? null) && (
                <p className="mt-1 flex items-center gap-1.5 text-[11px] text-muted-foreground">
                  <Clock className="h-3 w-3 shrink-0" />
                  Actualizado {formatUpdatedAt(promptQuery.data?.updated_at ?? null)}
                </p>
              )}
              {/* [D] Shared-prompt warning: in prod one prompt can back several
                  steps of the cadence. Editing it changes all of them, so say so. */}
              {(step.sharedByStepCount ?? 0) > 1 && (
                <p className="mt-2 flex items-start gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/[0.06] px-2.5 py-1.5 text-[11px] text-amber-700 dark:text-amber-300">
                  <Users className="h-3.5 w-3.5 mt-px shrink-0" />
                  <span>
                    Compartido por {step.sharedByStepCount} pasos de esta cadencia — editarlo
                    cambia todos.
                  </span>
                </p>
              )}
              {step.objective && (
                <p className="mt-1.5 flex items-start gap-1.5 text-xs text-foreground/80">
                  <Target className="h-3.5 w-3.5 mt-0.5 shrink-0 text-muted-foreground" />
                  <span>{step.objective}</span>
                </p>
              )}
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                {step.promptType && (
                  <Badge variant="secondary" className="text-[10px]">{step.promptType}</Badge>
                )}
                {step.language && (
                  <Badge variant="outline" className="text-[10px] uppercase">{step.language}</Badge>
                )}
                {step.tone && (
                  <Badge variant="outline" className="text-[10px]">{step.tone}</Badge>
                )}
                {step.bodyLength > 0 && (
                  <Badge variant="outline" className="text-[10px]">
                    {step.bodyLength.toLocaleString('es-ES')} chars
                  </Badge>
                )}
                {step.hasVariables && (
                  <Badge variant="outline" className="gap-1 text-[10px]">
                    <Braces className="h-3 w-3" />
                    con variables
                  </Badge>
                )}
              </div>
            </div>

            <ScrollArea className="flex-1">
              <div className="space-y-5 px-5 py-4">
                {/* ── Prompt body preview (read-only, purple {{var}} chips) ── */}
                <section>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Cuerpo del prompt
                  </h3>
                  {!promptId ? (
                    <p className="mt-2 text-sm text-muted-foreground">
                      Este paso no tiene un prompt asignado, así que no hay cuerpo que mostrar.
                    </p>
                  ) : promptQuery.isLoading ? (
                    <div className="mt-3 flex items-center gap-2 py-6 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" /> Cargando el prompt…
                    </div>
                  ) : promptQuery.error ? (
                    <p className="mt-2 flex items-center gap-1.5 text-sm text-red-600 dark:text-red-400">
                      <AlertTriangle className="h-4 w-4" /> No se pudo leer el cuerpo del prompt.
                    </p>
                  ) : !promptQuery.data ? (
                    // The summary (promptName) is resolved server-side by
                    // cadence-flow-metrics, which bypasses RLS. If that summary
                    // came through but the client's org-scoped SELECT returns
                    // null, the row exists — the reader just lacks permission
                    // (RLS), NOT a deletion. Only claim "ya no existe" when no
                    // summary was resolved at all.
                    step.promptName ? (
                      <p className="mt-2 flex items-center gap-1.5 text-sm text-muted-foreground">
                        <Lock className="h-4 w-4 shrink-0" />
                        No tienes permiso para ver el cuerpo de este prompt.
                      </p>
                    ) : (
                      <p className="mt-2 text-sm text-muted-foreground">
                        El prompt referenciado por este paso ya no existe.
                      </p>
                    )
                  ) : (
                    <>
                      <pre className="mt-2 max-h-[45vh] overflow-auto whitespace-pre-wrap break-words rounded-md border bg-muted/40 p-3 text-[13px] leading-relaxed font-sans">
                        {renderWithVariables(promptQuery.data.prompt_body)}
                      </pre>
                      {promptQuery.data.structure && promptQuery.data.structure.trim().length > 0 && (
                        <div className="mt-3">
                          <h4 className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                            <ListTree className="h-3.5 w-3.5" />
                            Estructura
                          </h4>
                          <p className="mt-1 text-[11px] text-muted-foreground">
                            El esqueleto que el motor le pide seguir a la IA al redactar este touch.
                          </p>
                          <pre className="mt-1.5 max-h-[30vh] overflow-auto whitespace-pre-wrap break-words rounded-md border bg-muted/40 p-3 text-[13px] leading-relaxed font-sans">
                            {renderWithVariables(promptQuery.data.structure)}
                          </pre>
                        </div>
                      )}
                      {(promptQuery.data.writing_principles.length > 0 ||
                        promptQuery.data.anti_patterns.length > 0) && (
                        <div className="mt-3 space-y-2">
                          {promptQuery.data.writing_principles.length > 0 && (
                            <div className="flex flex-wrap items-center gap-1.5">
                              <span className="text-[11px] font-medium text-muted-foreground">
                                Principios:
                              </span>
                              {promptQuery.data.writing_principles.map((p, i) => (
                                <Badge
                                  key={i}
                                  variant="outline"
                                  className="border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 text-[10px] font-normal"
                                >
                                  {p}
                                </Badge>
                              ))}
                            </div>
                          )}
                          {promptQuery.data.anti_patterns.length > 0 && (
                            <div className="flex flex-wrap items-center gap-1.5">
                              <span className="text-[11px] font-medium text-muted-foreground">
                                Anti-patrones:
                              </span>
                              {promptQuery.data.anti_patterns.map((p, i) => (
                                <Badge
                                  key={i}
                                  variant="outline"
                                  className="border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300 text-[10px] font-normal"
                                >
                                  {p}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </section>

                {/* ── What else gets injected ── */}
                <section className="rounded-md border bg-muted/30 p-3">
                  <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Qué más se inyecta
                  </h3>
                  <p className="mt-1.5 text-xs text-muted-foreground">
                    El prompt <strong>no es todo</strong> lo que ve la IA. El motor combina este
                    cuerpo con cuatro capas de contexto para producir el mensaje final:
                  </p>
                  <ul className="mt-2.5 space-y-2">
                    <InjectItem
                      icon={<UserRound className="h-3.5 w-3.5" />}
                      title="Persona del remitente"
                      detail="Quién firma el mensaje: nombre, cargo y voz del vendedor (sender persona de la org)."
                    />
                    <InjectItem
                      icon={<Radar className="h-3.5 w-3.5" />}
                      title="Señales del prospecto"
                      detail="Disparadores detectados (cambio de rol, contratación, noticias) que dan un motivo concreto de contacto."
                    />
                    {/* [D] Angle-lock reflects THIS day. A day with no
                        touch_angles row injects no angle at runtime, so don't
                        claim one is applied. */}
                    <InjectItem
                      icon={<Target className="h-3.5 w-3.5" />}
                      title="Angle-lock del touch"
                      detail={
                        step.hasAngle
                          ? 'La propuesta de valor asignada a este día (tab Ángulos): mantiene cada touch en un ángulo distinto y evita repetir el pitch.'
                          : 'Este día no tiene ángulo asignado → el motor no fuerza ninguno (asígnalo en Ángulos).'
                      }
                      muted={!step.hasAngle}
                    />
                    {/* [D] Research is injected as {{research}} only when this
                        step resolves a research prompt. */}
                    <InjectItem
                      icon={<Building2 className="h-3.5 w-3.5" />}
                      title="Research de la empresa"
                      detail={
                        step.hasResearchPrompt
                          ? 'El reporte de investigación del prospecto (stack de pagos, contexto de negocio) que aterriza el mensaje en su realidad.'
                          : 'Este paso no inyecta un prompt de research → el mensaje no recibe la variable {{research}}.'
                      }
                      muted={!step.hasResearchPrompt}
                    />
                  </ul>
                </section>

                {/* ── [D] How this step is judged — Carlos's step-specific rubric,
                    read-only. This is the system_prompt_addendum (e.g. the Day-9
                    ban on generic intros): the §2.2 layer-2 transparency ask is
                    "cómo se juzga este step" visible WITHOUT editing. To edit it,
                    the Política tab already holds the textarea. Rendered when the
                    step carries a non-empty addendum OR an effective rubric — the
                    EFFECTIVE values header is shown ALWAYS next to the prose so a
                    drifted instruction can never stand in for the real threshold. */}
                {(carlosAddendum || hasEffectiveRubric) && (
                  <section className="rounded-md border bg-muted/30 p-3">
                    <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      <Gavel className="h-3.5 w-3.5" />
                      Cómo se juzga este paso — rúbrica específica de Carlos
                    </h3>
                    {/* [D] The EFFECTIVE values Carlos applies — shown ALWAYS,
                        directly above the prose, because the column MANDA over the
                        text. If the addendum prose says "Threshold 8.0" but the
                        effective threshold is 7.0, this header keeps 7.0 in front
                        of the reader so the prose can't mislead. Only rendered when
                        an effective threshold resolved (no fabricated values). The
                        prose-vs-column drift warning lives in the Política tab
                        (StepRubricTable), where the addendum is edited. */}
                    {hasEffectiveRubric && (
                      <div className="mt-2 rounded-md border border-primary/30 bg-primary/[0.06] px-3 py-2 text-xs">
                        <span className="text-muted-foreground">
                          <span className="font-semibold text-foreground">
                            Valores efectivos que Carlos aplica:
                          </span>
                          {' umbral '}
                          <span className="font-mono">{carlosThreshold}</span>
                          {' · piso '}
                          <span className="font-mono">{carlosMinAcceptable ?? '—'}</span>
                          {' · '}
                          <span className="font-mono">{carlosMaxAttempts ?? '—'}</span>
                          {' reintentos'}
                        </span>
                        {/* [D] Labeled drift note: the addendum prose names a
                            threshold (via the WORD 'threshold'/'umbral'/'mínimo')
                            that differs from the effective one Carlos enforces.
                            Amber, small, advisory — the column MANDA. Only shown on
                            a LABELED mismatch; bare digits (e.g. the day number)
                            never trip it. */}
                        {proseThresholdDrift !== null && (
                          <p className="mt-1.5 flex items-start gap-1.5 text-[11px] text-amber-700 dark:text-amber-300">
                            <AlertTriangle className="mt-px h-3 w-3 shrink-0" />
                            <span>
                              La instrucción menciona un umbral (
                              <span className="font-mono">{proseThresholdDrift}</span>) distinto al
                              efectivo que Carlos aplica (
                              <span className="font-mono font-semibold">{carlosThreshold}</span>). Manda
                              la columna, no el texto.
                            </span>
                          </p>
                        )}
                      </div>
                    )}
                    {carlosAddendum && (
                      <>
                        <p className="mt-2.5 text-xs text-muted-foreground">
                          La instrucción que Carlos añade a su criterio general <strong>solo</strong>{' '}
                          para este touch. Es de solo lectura aquí; para editarla, ve al tab Política.
                        </p>
                        <blockquote className="mt-2.5 border-l-2 border-primary/40 pl-3">
                          <pre className="whitespace-pre-wrap break-words text-[13px] leading-relaxed font-sans text-foreground/80">
                            {carlosAddendum}
                          </pre>
                        </blockquote>
                      </>
                    )}
                  </section>
                )}

                {/* ── Engine rules — read-only accordion ── */}
                <CollapsibleSection
                  title="Reglas del motor (no editables)"
                  description="Invariantes que el motor añade por encima del prompt"
                  icon={<Shield className="h-4 w-4" />}
                >
                  <p className="text-xs text-muted-foreground">
                    Además del prompt y del contexto, el motor aplica siempre estas reglas
                    deterministas <strong>antes</strong> de que Carlos puntúe. No son política de la
                    org: son invariantes de marca y de formato, por eso son de solo lectura aquí.
                  </p>
                  <ul className="mt-1 space-y-1.5 text-xs text-muted-foreground">
                    <RuleItem>Prohibición de em-dashes y de vocabulario de IA (delve, leverage, unlock…).</RuleItem>
                    <RuleItem>Lock de posicionamiento: primer touch vs. respuesta en hilo se abren distinto.</RuleItem>
                    <RuleItem>Zero-placeholder: ninguna variable <code>{'{{...}}'}</code> puede quedar sin resolver.</RuleItem>
                    <RuleItem>Angle-lock del día: el mensaje debe usar el ángulo asignado a este touch.</RuleItem>
                    <RuleItem>Reglas por canal: longitud y formato propios de LinkedIn vs. email.</RuleItem>
                  </ul>
                </CollapsibleSection>
              </div>
            </ScrollArea>

            {/* Footer */}
            <div className="flex items-center justify-between gap-2 border-t px-5 py-3">
              <SheetClose asChild>
                <Button variant="ghost" size="sm">Cerrar</Button>
              </SheetClose>
              {canEditPrompts ? (
                <Button asChild variant="outline" size="sm">
                  {/* Deep-link the exact prompt so AIPrompts can pre-open its editor
                      (?prompt=<id> read via useSearchParams). Falls back to the bare
                      tab when the step carries no prompt id. */}
                  <Link to={promptId ? `/ai-prompts?prompt=${promptId}` : '/ai-prompts'}>
                    <ExternalLink className="mr-1.5 h-4 w-4" />
                    Editar prompt
                  </Link>
                </Button>
              ) : (
                // section_ai_prompts is off for this org → /ai-prompts would dead-end
                // on the home page. Don't render a dead link; the body above already
                // shows the prompt read-only (transparency intact), so just say so.
                <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Lock className="h-3.5 w-3.5 shrink-0" />
                  La edición de la copy de prompts está deshabilitada para esta organización — se gestiona con un administrador.
                </span>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  )
}

// `muted` marks an injection layer that does NOT apply to this step (e.g. a day
// with no assigned angle): dimmed + title struck through so the reader sees at a
// glance the layer is inactive here, while the detail explains why.
function InjectItem({
  icon,
  title,
  detail,
  muted = false,
}: {
  icon: React.ReactNode
  title: string
  detail: string
  muted?: boolean
}) {
  return (
    <li className={`flex items-start gap-2${muted ? ' opacity-70' : ''}`}>
      <span className="mt-0.5 text-muted-foreground shrink-0">{icon}</span>
      <span className="text-xs">
        <span className={`font-medium${muted ? ' line-through decoration-muted-foreground/50' : ''}`}>
          {title}.
        </span>{' '}
        <span className="text-muted-foreground">{detail}</span>
      </span>
    </li>
  )
}

function RuleItem({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-1.5">
      <span className="mt-1 h-1 w-1 shrink-0 rounded-full bg-muted-foreground/50" />
      <span>{children}</span>
    </li>
  )
}
